import { Observable, Subject } from 'rxjs';
import { filter, finalize, take } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationExtras } from '@angular/router';

import { KillOnDestroy } from '@sg/utils';

import { SgAuxRoute, SgAuxRouteNavigationOptions, SgAuxRouteCommand } from './models';
import { SgAuxRouteBridgeEvent, SgAuxRouteBridgeCreateUpdateEvent, SgAuxRouteBridgeDestroyEvent } from './events';
import { SgAuxRouteBridgeService } from './aux-route-bridge.service';

@Injectable({
  providedIn: 'root',
})
@KillOnDestroy()
export class SgAuxRouter {
  readonly events: Observable<SgAuxRouteBridgeEvent>;

  constructor(private router: Router, private bridge: SgAuxRouteBridgeService) {
    this.events = bridge.events;
  }

  defineRoute<T>(config: SgAuxRoute<T>): void {
    this.bridge.defineRoute(config);
  }

  toggleRoute(
    outlet: string,
    path: string,
    options?: SgAuxRouteNavigationOptions
  ): Observable<SgAuxRouteBridgeCreateUpdateEvent | false | SgAuxRouteBridgeDestroyEvent> {
    if (this.bridge.isHostActive(outlet)) {
      return this.clearRoute(outlet);
    } else {
      return this.setRoute(outlet, path, options);
    }
  }

  getRouteCommand(outlet: string, path: string, options: SgAuxRouteNavigationOptions = {}): any[] {
    const outletsSetup: any = {
      [outlet]: [path],
    };

    if (options && options.params) {
      outletsSetup[outlet].push(options.params);
    }

    const bridgeOutlet = this.bridge.getOutlet(outlet);
    const context: string[] = bridgeOutlet ? bridgeOutlet.relativeRoot : [];

    return [
      ...context,
      {
        outlets: outletsSetup,
      },
    ];
  }

  /**
   * Get the URL to an aux route
   */
  getRoute(outlet: string, path: string, options: SgAuxRouteNavigationOptions = {}): string {
    const navigationExtras = options.navigationExtras || this.navigationExtras();
    return this.router.createUrlTree(this.getRouteCommand(outlet, path, options), navigationExtras).toString();
  }

  buildAuxCommand(outlet: string, path: string, options: SgAuxRouteNavigationOptions = {}): SgAuxRouteCommand {
    return new SgAuxRouteCommand(this, outlet, path, options);
  }

  setRoute<T = any>(
    outlet: string,
    path: string,
    options: SgAuxRouteNavigationOptions = {}
  ): Observable<SgAuxRouteBridgeCreateUpdateEvent<T> | false> {
    KillOnDestroy.kill(this, outlet);
    if (!options.navigationExtras) {
      options.navigationExtras = this.navigationExtras();
    }

    const subject = new Subject<SgAuxRouteBridgeCreateUpdateEvent | false>();
    const url = this.getRoute(outlet, path, options);

    let emitted = false;
    const emit = (err: Error | null, result?: SgAuxRouteBridgeCreateUpdateEvent<T> | false) => {
      if (!emitted) {
        emitted = true;
        if (err) {
          subject.error(err);
        } else {
          subject.next(result);
          subject.complete();
        }
      }
    };

    this.autoClearNextNavigation(outlet, path)
      .then((event) => emit(null, event))
      .catch((err) => emit(err));

    this.router
      .navigateByUrl(url, options.navigationExtras)
      .then((result) => {
        if (!result) {
          emit(null, false);
        }
      })
      .catch((err) => emit(err));

    return subject.asObservable().pipe(finalize(() => KillOnDestroy.kill(this, outlet))) as any;
  }

  clearRoute(outlet: string): Observable<SgAuxRouteBridgeDestroyEvent> {
    KillOnDestroy.kill(this, outlet);
    const navigationExtras = this.navigationExtras();
    const outletsSetup: any = {
      [outlet]: null,
    };

    const bridgeOutlet = this.bridge.getOutlet(outlet);
    const context: string[] = bridgeOutlet ? bridgeOutlet.relativeRoot : [];

    const urlTree = this.router.createUrlTree(
      [
        ...context,
        {
          outlets: outletsSetup,
        },
      ],
      navigationExtras
    );

    const primary = urlTree.root.children.primary;
    if (primary.children.primary) {
      primary.segments.push(...primary.children.primary.segments);
      delete primary.children.primary;
    }

    const subject = new Subject<SgAuxRouteBridgeDestroyEvent>();
    this.events
      .pipe(
        filter((event) => event.outlet === outlet),
        take(1),
        KillOnDestroy(this, outlet)
      )
      .subscribe((event) => {
        if (event.kind === 'destroy') {
          subject.next(event);
          subject.complete();
        } else {
          subject.error(new Error(`Invalid operation, route for outlet ${outlet} was not cleared properly.`));
        }
      });

    this.router.navigateByUrl(urlTree.toString(), navigationExtras);
    return subject.asObservable();
  }

  closeRouteFromInstance(instance: any): boolean {
    const outlet = this.bridge.findOutletByInstance(instance);
    if (outlet) {
      this.clearRoute(outlet);
      return true;
    }
    return false;
  }

  /**
   * Wait for the next `NavigationStart` event from the `Router` and when emitted waits
   * for the `createOrUpdate` from the `SgAuxRouter and when emitted will register an handler to
   * automatically clear the route on the following navigation.
   * @param outlet
   * @param path
   */
  autoClearNextNavigation(outlet: string, path: string): Promise<SgAuxRouteBridgeCreateUpdateEvent> {
    return new Promise<SgAuxRouteBridgeCreateUpdateEvent>((resolve, reject) => {
      let phase = 0;
      const onComplete = (id: number) => {
        if (phase === 0 && id === 0) {
          reject(new Error('Expected router event "NavigationStart".'));
        } else if (phase === 1 && id === 1) {
          reject(new Error('Outlet destroyed before navigation.'));
        }
      };

      // TODO: provide cancellation option and make sure no memory leaks, e.g. clean up on rejects...
      // Also return a more complex result that also emits on clear navigation.
      this.router.events
        .pipe(
          filter((e) => e instanceof NavigationStart),
          take(1),
          KillOnDestroy(this, outlet)
        )
        .subscribe(
          (navStart: NavigationStart) => {
            phase = 1;
            this.events
              .pipe(
                filter((event) => event.outlet === outlet && event.kind === 'createOrUpdate'),
                take(1),
                KillOnDestroy(this, outlet)
              )
              .subscribe(
                (event) => {
                  phase = 2;
                  if (event.kind === 'createOrUpdate' && event.path === path) {
                    // Adding a router navigation listener to close the outlet on navigation.
                    // On navigation it will call clearRoute, which will terminate all subscriptions.
                    // Registering the listener after completing the subject is required or the "completion" will unsubscribe the listener.
                    // We use a predicate to prevent clearing when the navigation end event is the event triggered this subscription...
                    // This will happen when we navigate to the same aux path but with changed params/route etc...
                    this.clearOnNavigation(outlet, (navEnd) => navEnd.id !== navStart.id);
                    resolve(event);
                  } else {
                    reject(new Error(`Invalid operation, outlet ${outlet} with route ${path} was not set properly.`));
                  }
                },
                undefined,
                () => onComplete(1)
              );
          },
          undefined,
          () => onComplete(0)
        );
    });
  }

  private clearOnNavigation(outlet: string, canClear?: (navEnd: NavigationEnd) => boolean): void {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        KillOnDestroy(this, outlet)
      )
      .subscribe((navEnd: NavigationEnd) => {
        const clear = typeof canClear === 'function' ? canClear(navEnd) : true;
        if (clear) {
          this.clearRoute(outlet);
        }
      });
  }

  private navigationExtras(): NavigationExtras {
    return {
      relativeTo: this.router.routerState.root,
      preserveFragment: true,
      queryParamsHandling: 'preserve',
    };
  }
}
